import '@/utils/zodCompat';
import { GTMInit } from '@/domains/shared/analytics/google-tag-manager/googleTagManager';
import { useDatadogLogs } from '@/domains/shared/analytics/datadogUtils';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import AuthProvider from '@/domains/auth/authContext';
import '@/styles/globals.css';
import {
  THEME_PREFERENCE_CHANGE_EVENT,
  applyThemeClass,
  loadThemePreference,
} from '@/domains/theme/applyThemeClass';
import {
  THEME_PREFERENCE_KEY,
  type ThemePreference,
} from '@/domains/theme/constants';
import { syncStatusBarStyle } from '@/domains/theme/syncStatusBarStyle';
import type { AppProps } from 'next/app';
import {
  useRef,
  type ReactElement,
  type ReactNode,
  useState,
  useEffect,
  useCallback,
} from 'react';
import type { NextPage } from 'next';
import Head from 'next/head';
import DefaultLayout from '@/domains/shared/components/layouts/DefaultLayout';
import 'react-international-phone/style.css';
import { ToastProvider } from '@/domains/shared/components/toast/ToastProvider';
import { CapabilitiesProvider } from '@/domains/shared/components/AccountAccess/CapabilitiesContext';
import { FeatureFlagsProvider } from '@/domains/shared/components/AccountAccess/FeatureFlagsContext';
import AccountProvider from '@/domains/shared/components/AccountAccess/AccountContext';
import ForceUpdateScreen from '@/domains/shared/components/ForceUpdateScreen';
import 'react-toastify/dist/ReactToastify.css';
import '@/domains/shared/components/toast/Toast.scss';
import './styles.css';
import { Network, ConnectionStatus } from '@capacitor/network';
import LoadingScreen from '@/domains/shared/components/LoadingScreen';
import { LoadingBoundary } from '@/domains/shared/components/LoadingBoundary';
import { useRouter } from 'next/router';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import NoNetworkScreen from './hive/native/no-network-screen';
import { customGetAuth } from '@/services/firebase-service/firebase-client';
import { type Auth } from 'firebase/auth';
import PinManager from '@/domains/shared/components/pin/PinManager';
import { BiometricsProvider } from '@/domains/shared/components/biometrics/BiometricsContext';
import { IdentityBootProvider } from '@/domains/shared/identity/IdentityBootContext';
import { DeviceSecurityDetect } from '@capacitor-community/device-security-detect';
import { Device } from '@capacitor/device';
import JailbrokenOrRootedScreen from '@/pages/hive/native/jailbroken-or-rooted-screen';
import { logToDatadog } from '@/domains/shared/analytics/datadogUtils';
import { IntercomMessenger } from '../domains/comms';
import { PinProvider } from '@/domains/shared/components/pin/PinContext';
import { BetaFeaturesProvider } from '@/domains/shared/components/AccountAccess/BetaFeaturesContext';
import '@/domains/shared/appEvents/handlers';
import { trackAppSession } from '@/services/rate-service/rate';
import { useBootstrap } from '@/domains/shared/bootstrap/useBootstrap';
import { useTextZoom } from '@/utils/useTextZoom';
import { useKeyboardDismiss } from '@/domains/shared/hooks/useKeyboardDismiss';
import { DeepLinkListener } from '@/domains/deep-linking';
import { enableCrashlytics } from '@/domains/shared/analytics/crashlytics/enableCrashlytics';
import { getPlatform } from '@/domains/shared/utils/native';
import { CapabilityAccessGate } from '@/domains/shared/components/AccountAccess/CapabilityAccessGate';
import { usePlatformInfo } from '@/domains/shared/hooks/useIsNative';
// Extend AppProps to support per-page layouts
export type NextPageWithLayout<P = Record<string, unknown>> = NextPage<P> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

export default function App({ Component, pageProps }: AppPropsWithLayout) {
  const { asPath, isReady } = useRouter();
  const [isOnline, setIsOnline] = useState<boolean>(true);
  const [isRooted, setIsRooted] = useState<boolean>(false);
  const [auth, setAuth] = useState<Auth>();
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
          },
        },
      }),
  );
  const trackingReady = useBootstrap();
  useTextZoom();
  useKeyboardDismiss();

  const { isNative, isIOS } = usePlatformInfo();

  useEffect(() => {
    const applyFromPreference = () =>
      loadThemePreference().then((preference) => {
        applyThemeClass(preference);
        syncStatusBarStyle();
      });

    // Apply the persisted theme on every page load, app-wide and on all
    // platforms:
    applyFromPreference();

    // Same-tab changes: `useThemePreference` dispatches this custom event
    // because the DOM `storage` event never fires in the tab that made the
    // change (and never at all on native, where the value lives in Capacitor
    // Preferences rather than localStorage).
    const onThemeChange = (event: Event) => {
      const preference = (event as CustomEvent<ThemePreference>).detail;
      applyThemeClass(preference);
      syncStatusBarStyle();
    };
    // Cross-tab sync on web only.
    const onStorage = (event: StorageEvent) => {
      if (event.key !== THEME_PREFERENCE_KEY) return;
      applyFromPreference();
    };
    window.addEventListener(THEME_PREFERENCE_CHANGE_EVENT, onThemeChange);
    window.addEventListener('storage', onStorage);

    return () => {
      window.removeEventListener(THEME_PREFERENCE_CHANGE_EVENT, onThemeChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  useEffect(() => {
    if (!isNative) return;
    if (!isReady) return;
    logToDatadog('info', `Screen view recorded on mobile - ${asPath}`);
  }, [isReady, asPath, isNative]);

  useEffect(() => {
    document.body.dataset.platform = getPlatform();

    Network.getStatus().then((status: ConnectionStatus) => {
      setIsOnline(status.connected);
    });

    const listener = Network.addListener(
      'networkStatusChange',
      (status: ConnectionStatus) => {
        setIsOnline(status.connected);
      },
    );

    return () => {
      listener.then((handle) => handle.remove());
    };
  }, []);

  useEffect(() => {
    customGetAuth().then((authInstance) => {
      setAuth(authInstance);
    });
  }, []);

  const getLayout =
    Component.getLayout ?? ((page) => <DefaultLayout>{page}</DefaultLayout>);

  useDatadogLogs();

  const checkRootStatus = useCallback(async () => {
    if (!isNative) return;

    try {
      // Check if we're running on a simulator/emulator
      // iOS simulators and Android emulators often trigger false positives
      const deviceInfo = await Device.getInfo();
      const modelLower = deviceInfo.model.toLowerCase();
      const isSimulator =
        modelLower.includes('simulator') ||
        modelLower.includes('emulator') ||
        // Check for common iOS simulator identifiers
        (isIOS &&
          (deviceInfo.model.includes('Simulator') ||
            // iOS simulators on Intel Macs
            deviceInfo.model.includes('x86_64')));

      if (
        isSimulator ||
        process.env.NEXT_PUBLIC_PLATFORM_APP_ENV === 'dev' ||
        process.env.NEXT_PUBLIC_PLATFORM_APP_ENV === 'staging'
      ) {
        console.info(
          'Running on simulator/emulator - skipping jailbreak/root detection',
        );
        setIsRooted(false);
        return;
      }

      const result = await DeviceSecurityDetect.isJailBreakOrRooted();
      if (result.value) {
        console.warn('The device is rooted or jailbroken!');
      } else {
        console.info('The device is secure.');
      }
      setIsRooted(result.value);
    } catch (error) {
      console.error('Error checking root status:', error);
      // On error, assume device is secure to avoid blocking users
      setIsRooted(false);
    }
  }, [isNative, isIOS]);

  useEffect(() => {
    checkRootStatus();
  }, [checkRootStatus]);

  const hasTrackedSession = useRef(false);

  useEffect(() => {
    if (hasTrackedSession.current) return;
    hasTrackedSession.current = true;

    void trackAppSession();
  }, []);

  useEffect(() => {
    void enableCrashlytics();
  }, []);

  return (
    <div>
      <QueryClientProvider client={queryClient}>
        {process.env.NODE_ENV === 'development' && <ReactQueryDevtools />}
        {trackingReady && <GTMInit />}
        <AuthProvider auth={auth}>
          <ForceUpdateScreen>
            <IdentityBootProvider>
              <AccountProvider>
                <CapabilitiesProvider>
                  <BiometricsProvider>
                    <FeatureFlagsProvider>
                      <BetaFeaturesProvider>
                        <PinProvider>
                          <PinManager>
                            <IntercomMessenger />
                            <DeepLinkListener />
                            <Head>
                              <meta name="robots" content="noindex" />
                              <meta
                                name="viewport"
                                content="width=device-width, initial-scale=1, viewport-fit=cover"
                              />
                            </Head>
                            {!isOnline ? (
                              <NoNetworkScreen />
                            ) : isRooted ? (
                              <JailbrokenOrRootedScreen />
                            ) : (
                              <CapabilityAccessGate>
                                {getLayout(
                                  <LoadingBoundary
                                    fallback={<LoadingScreen key={asPath} />}
                                    onError={(error, info) =>
                                      logToDatadog(
                                        'error',
                                        'Uncaught render error',
                                        {
                                          error,
                                          componentStack: info.componentStack,
                                        },
                                      )
                                    }
                                  >
                                    <Component {...pageProps} />
                                  </LoadingBoundary>,
                                )}
                              </CapabilityAccessGate>
                            )}
                            <ToastProvider />
                          </PinManager>
                        </PinProvider>
                      </BetaFeaturesProvider>
                    </FeatureFlagsProvider>
                  </BiometricsProvider>
                </CapabilitiesProvider>
              </AccountProvider>
            </IdentityBootProvider>
          </ForceUpdateScreen>
        </AuthProvider>
      </QueryClientProvider>
    </div>
  );
}
